import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Database,
  Activity,
  HardDrive,
  Clock,
  Loader2,
  Layers,
  ShieldCheck,
  Server,
  BarChart2,
  RotateCcw,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Label,
} from 'recharts';
import baseapi from '../../../services/baseapi';

interface MongoWiredTigerViewProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 max-h-14 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
              }`}
          >
            <span
              className="w-2.5 h-2.5 rounded-full inline-block shrink-0 shadow-sm"
              style={{ backgroundColor: entry.color }}
            />
            <span
              className="text-gray-700 dark:text-gray-200 text-xs truncate max-w-[160px] font-mono font-medium"
              title={entry.value}
            >
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const MongoWiredTigerView: React.FC<MongoWiredTigerViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [wtData, setWtData] = useState<any>(null);
  const [fsyncData, setFsyncData] = useState<any>(null);

  const [isDark, setIsDark] = useState<boolean>(() =>
    typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : true
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Active Chart Category Tab
  const [activeChartTab, setActiveChartTab] = useState<'cache' | 'page_io' | 'transactions' | 'journal' | 'concurrency'>('cache');
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);
  const [queryThroughputData, setQueryThroughputData] = useState<any>(null);
  const [queryDurationData, setQueryDurationData] = useState<any>(null);
  const [hoveredThroughput, setHoveredThroughput] = useState<string | null>(null);
  const [hoveredDuration, setHoveredDuration] = useState<string | null>(null);

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.15;
  };

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60', 10);
    const params: any = {
      cluster_id: activeCluster,
      
      minutes,
    };
    if (clientId) params.client_id = clientId;

    try {
      const [res, qtRes, qdRes] = await Promise.all([
        baseapi.get('/db_monitor_dashboard/graph/mongodb/wiredtiger', { params }),
        baseapi.get('/db_monitor_dashboard/graph/query_throughput', { params }).catch(() => null),
        baseapi.get('/db_monitor_dashboard/graph/query_duration', { params }).catch(() => null),
      ]);
      const raw = res.data;
      if (raw?.status && raw?.data) {
        setWtData(raw.data);
        if (raw.fsync) setFsyncData(raw.fsync);
      } else if (raw?.labels && raw?.series) {
        setWtData(raw);
        if (raw.fsync) setFsyncData(raw.fsync);
      } else {
        setWtData(null);
        setFsyncData(null);
      }

      setQueryThroughputData(qtRes?.data?.data || qtRes?.data || null);
      setQueryDurationData(qdRes?.data?.data || qdRes?.data || null);
    } catch (err) {
      console.warn('MongoWiredTigerView fetch error:', err);
      setWtData(null);
      setFsyncData(null);
      setQueryThroughputData(null);
      setQueryDurationData(null);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const labels: string[] = wtData?.labels || [];
  const series: Record<string, (number | null)[]> = wtData?.series || {};
  const summary = wtData?.summary;
  const latest = summary?.latest;
  const peak = summary?.peak;
  const avg = summary?.average;

  // Format Time labels
  const formattedTimeLabels = useMemo(() => {
    if (!labels || labels.length === 0) return [];
    const minutes = parseInt(timeRange || '60', 10);
    const seen: { [key: string]: number } = {};
    return labels.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  }, [labels, timeRange]);

  // Chart datasets
  const cacheChartData = useMemo(() => {
    if (!labels || labels.length === 0) return [];
    return labels.map((_, idx) => ({
      time: formattedTimeLabels[idx] || labels[idx],
      'Cache Used (MB)': series.cache_used_mb?.[idx] ?? 0,
      'Cache Dirty (MB)': series.cache_dirty_mb?.[idx] ?? 0,
      'Cache Max (MB)': series.cache_max_mb?.[idx] ?? 0,
      'Used %': series.cache_used_pct?.[idx] ?? 0,
    }));
  }, [labels, series, formattedTimeLabels]);

  const pageIoChartData = useMemo(() => {
    if (!labels || labels.length === 0) return [];
    return labels.map((_, idx) => ({
      time: formattedTimeLabels[idx] || labels[idx],
      'Pages Read/s': series.pages_read_per_sec?.[idx] ?? 0,
      'Pages Written/s': series.pages_written_per_sec?.[idx] ?? 0,
      'Evicted Modified/s': series.evicted_modified_per_sec?.[idx] ?? 0,
      'Evicted Unmodified/s': series.evicted_unmodified_per_sec?.[idx] ?? 0,
    }));
  }, [labels, series, formattedTimeLabels]);

  const transactionsChartData = useMemo(() => {
    if (!labels || labels.length === 0) return [];
    return labels.map((_, idx) => ({
      time: formattedTimeLabels[idx] || labels[idx],
      'Txn Committed/s': series.txn_committed_per_sec?.[idx] ?? 0,
      'Txn Rolled Back/s': series.txn_rolled_back_per_sec?.[idx] ?? 0,
      'Update Conflicts/s': series.update_conflicts_per_sec?.[idx] ?? 0,
    }));
  }, [labels, series, formattedTimeLabels]);

  const journalChartData = useMemo(() => {
    if (!labels || labels.length === 0) return [];
    return labels.map((_, idx) => ({
      time: formattedTimeLabels[idx] || labels[idx],
      'Sync Avg Latency (ms)': series.journal_sync_avg_ms?.[idx] ?? 0,
      'Journal KB/s': series.journal_kb_per_sec?.[idx] ?? 0,
      'Journal Syncs/s': series.journal_syncs_per_sec?.[idx] ?? 0,
    }));
  }, [labels, series, formattedTimeLabels]);

  const concurrencyChartData = useMemo(() => {
    if (!labels || labels.length === 0) return [];
    return labels.map((_, idx) => ({
      time: formattedTimeLabels[idx] || labels[idx],
      'Open Sessions': series.open_sessions?.[idx] ?? 0,
      'Open Cursors': series.open_cursors?.[idx] ?? 0,
      'Read Tickets': series.read_tickets_available?.[idx] ?? 0,
      'Write Tickets': series.write_tickets_available?.[idx] ?? 0,
    }));
  }, [labels, series, formattedTimeLabels]);

  const throughputTimeLabels = useMemo(() => {
    if (!queryThroughputData?.labels || queryThroughputData.labels.length === 0) return [];
    const minutes = parseInt(timeRange || '60', 10);
    const seen: { [key: string]: number } = {};
    return queryThroughputData.labels.map((lbl: string) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  }, [queryThroughputData, timeRange]);

  const throughputFormatted = useMemo(() => {
    if (!queryThroughputData?.labels || queryThroughputData.labels.length === 0) return [];
    return (queryThroughputData.labels || []).map((_: string, idx: number) => {
      const point: any = { time: throughputTimeLabels[idx] || queryThroughputData.labels[idx] };
      (queryThroughputData?.query_types || []).forEach((qt: string) => {
        point[qt.toUpperCase()] = Number((queryThroughputData?.series?.[qt]?.[idx] || 0).toFixed(2));
      });
      return point;
    });
  }, [queryThroughputData, throughputTimeLabels]);

  const durationTimeLabels = useMemo(() => {
    if (!queryDurationData?.labels || queryDurationData.labels.length === 0) return [];
    const minutes = parseInt(timeRange || '60', 10);
    const seen: { [key: string]: number } = {};
    return queryDurationData.labels.map((lbl: string) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  }, [queryDurationData, timeRange]);

  const durationFormatted = useMemo(() => {
    if (!queryDurationData?.labels || queryDurationData.labels.length === 0) return [];
    return (queryDurationData.labels || []).map((_: string, idx: number) => {
      const point: any = { time: durationTimeLabels[idx] || queryDurationData.labels[idx] };
      (queryDurationData?.query_types || []).forEach((qt: string) => {
        point[qt.toUpperCase()] = Number((queryDurationData?.series?.[qt]?.[idx] || 0).toFixed(2));
      });
      return point;
    });
  }, [queryDurationData, durationTimeLabels]);

  const colors = ['#3b82f6', '#10b981', '#a855f7', '#f59e0b', '#ef4444', '#06b6d4'];

  const hasData = (arr: any[]) => {
    if (!arr || arr.length === 0) return false;
    return arr.some(item => Object.keys(item).some(k => k !== 'time' && typeof item[k] === 'number' && item[k] > 0));
  };

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[400px]">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header View */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-800 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-lg bg-orange-500/10 border border-orange-500/30 text-orange-500 dark:text-orange-400">
            <Database className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-gray-900 dark:text-white tracking-wide">
                Database Performance (WiredTiger Engine)
              </h2>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-orange-500/20 text-orange-600 dark:text-orange-400 border border-orange-500/30 font-mono">
                MongoDB Storage & Cache
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Live cache memory utilization, page evictions, concurrent execution tickets, transaction rates, and journal sync latency.
            </p>
          </div>
        </div>

        {/* Fsync Node Status Badge */}
        {fsyncData && (
          <div className="flex items-center gap-2 font-mono text-xs">
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-gray-800/80 border border-gray-300 dark:border-gray-700/80 rounded-lg text-gray-700 dark:text-gray-300">
              <Server className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
              <span>{fsyncData.nodes?.[0]?.host || 'Node'}:{fsyncData.nodes?.[0]?.port || '27017'}</span>
              <span className="text-gray-400 dark:text-gray-500">•</span>
              <span className="text-gray-500 dark:text-gray-400 font-sans capitalize">{fsyncData.nodes?.[0]?.role || 'Standalone'}</span>
            </div>
            <div
              className={`flex items-center gap-1 px-2 py-1 rounded-lg border ${fsyncData.locked
                ? 'bg-rose-500/10 border-rose-500/30 text-rose-600 dark:text-rose-400'
                : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400'
                }`}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>{fsyncData.locked ? 'Fsync Locked' : 'Fsync Unlocked'}</span>
            </div>
          </div>
        )}
      </div>

      {/* KPI Overview Telemetry Cards (Compact Grid) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
        {/* CACHE USAGE */}
        <div className="bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-900/90 dark:to-gray-900/60 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-orange-500/30 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <HardDrive className="w-3 h-3 text-orange-500 dark:text-orange-400" />
              Cache Usage
            </span>
            <span
              className={`text-[9px] font-mono px-1.5 py-0.2 rounded font-semibold border ${(latest?.cache_used_pct || 0) > 85
                ? 'bg-rose-500/20 text-rose-600 dark:text-rose-400 border-rose-500/30'
                : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                }`}
            >
              {latest?.cache_used_pct ?? 0}%
            </span>
          </div>
          <div className="text-xl font-bold font-mono text-gray-900 dark:text-white tracking-tight my-0.5">
            {latest?.cache_used_mb ?? 0} <span className="text-xs text-gray-400 font-normal">/ {latest?.cache_max_mb ?? 0} MB</span>
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-200 dark:border-gray-800/80 truncate">
            Dirty: <strong className="text-amber-600 dark:text-amber-400 font-mono">{latest?.cache_dirty_mb ?? 0} MB</strong> ({latest?.cache_dirty_pct ?? 0}%)
          </div>
        </div>

        {/* WRITE / READ TICKETS */}
        <div className="bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-900/90 dark:to-gray-900/60 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-emerald-500/30 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <Activity className="w-3 h-3 text-emerald-500 dark:text-emerald-400" />
              Tickets Avail
            </span>
            <span className="text-[9px] font-mono text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1 py-0.2 rounded font-bold">
              Clean
            </span>
          </div>
          <div className="text-xl font-bold font-mono text-emerald-600 dark:text-emerald-400 tracking-tight my-0.5">
            R: {latest?.read_tickets_available ?? 0} <span className="text-xs text-gray-400 font-normal">| W: {latest?.write_tickets_available ?? 0}</span>
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-200 dark:border-gray-800/80 truncate">
            Read: {latest?.read_tickets_used_pct ?? 0}% • Write: {latest?.write_tickets_used_pct ?? 0}%
          </div>
        </div>

        {/* SESSIONS & CURSORS */}
        <div className="bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-900/90 dark:to-gray-900/60 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-blue-500/30 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <Layers className="w-3 h-3 text-blue-500 dark:text-blue-400" />
              Sessions
            </span>
          </div>
          <div className="text-xl font-bold font-mono text-blue-600 dark:text-blue-400 tracking-tight my-0.5">
            {latest?.open_sessions ?? 0} <span className="text-xs text-gray-400 font-normal">active</span>
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-200 dark:border-gray-800/80 truncate">
            Open Cursors: <strong className="text-gray-900 dark:text-white font-mono">{latest?.open_cursors ?? 0}</strong>
          </div>
        </div>

        {/* PAGE WRITES & READS */}
        <div className="bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-900/90 dark:to-gray-900/60 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-purple-500/30 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <HardDrive className="w-3 h-3 text-purple-500 dark:text-purple-400" />
              Page I/O Rate
            </span>
          </div>
          <div className="text-xl font-bold font-mono text-purple-600 dark:text-purple-400 tracking-tight my-0.5">
            {latest?.pages_written_per_sec ?? 0} <span className="text-xs text-gray-400 font-normal">writes/s</span>
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-200 dark:border-gray-800/80 truncate">
            Reads: <strong className="text-gray-800 dark:text-gray-200 font-mono">{latest?.pages_read_per_sec ?? 0}/s</strong>
          </div>
        </div>

        {/* TRANSACTION ROLLBACKS */}
        <div className="bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-900/90 dark:to-gray-900/60 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-rose-500/30 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <RotateCcw className="w-3 h-3 text-rose-500 dark:text-rose-400" />
              Aborted transactions
            </span>
          </div>
          <div className="text-xl font-bold font-mono text-rose-600 dark:text-rose-400 tracking-tight my-0.5">
            {latest?.txn_rolled_back_per_sec ?? 0} <span className="text-xs text-gray-400 font-normal">/s</span>
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-200 dark:border-gray-800/80 truncate">
            Committed: <strong className="text-gray-800 dark:text-gray-200 font-mono">{latest?.txn_committed_per_sec ?? 0}/s</strong>
          </div>
        </div>

        {/* JOURNAL SYNC LATENCY */}
        <div className="bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-900/90 dark:to-gray-900/60 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-amber-500/30 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <Clock className="w-3 h-3 text-amber-500 dark:text-amber-400" />
              Journal Sync
            </span>
            <span className="text-[9px] font-mono text-amber-600 dark:text-amber-400 bg-amber-500/10 px-1 py-0.2 rounded font-bold">
              {latest?.journal_sync_avg_ms ?? 0} ms
            </span>
          </div>
          <div className="text-xl font-bold font-mono text-amber-600 dark:text-amber-400 tracking-tight my-0.5">
            {latest?.journal_kb_per_sec ?? 0} <span className="text-xs text-gray-400 font-normal">KB/s</span>
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-200 dark:border-gray-800/80 truncate">
            Checkpoints: <strong className="text-emerald-600 dark:text-emerald-400 font-mono">{latest?.checkpoints_succeeded ?? 0}</strong>
          </div>
        </div>
      </div>

      {/* Main Interactive Telemetry Graph Section */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700/50 pb-3">
          <div>
            <div className="flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-orange-500 dark:text-orange-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                WiredTiger Engine Telemetry Timeline
              </h3>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              Historical engine performance, caching dynamics, and I/O concurrency
            </p>
          </div>

          {/* Metric View Tabs */}
          <div className="flex flex-wrap items-center gap-1 bg-gray-100 dark:bg-gray-900/90 p-1 rounded-lg border border-gray-200 dark:border-gray-700/80">
            <button
              onClick={() => setActiveChartTab('cache')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${activeChartTab === 'cache'
                ? 'bg-orange-600 text-white font-bold shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              Cache & Memory
            </button>
            <button
              onClick={() => setActiveChartTab('page_io')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${activeChartTab === 'page_io'
                ? 'bg-purple-600 text-white font-bold shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              Page I/O & Evictions
            </button>
            <button
              onClick={() => setActiveChartTab('transactions')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${activeChartTab === 'transactions'
                ? 'bg-rose-600 text-white font-bold shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              Transactions & Rollbacks
            </button>
            <button
              onClick={() => setActiveChartTab('journal')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${activeChartTab === 'journal'
                ? 'bg-amber-600 text-white font-bold shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              Journaling & Sync
            </button>
            <button
              onClick={() => setActiveChartTab('concurrency')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${activeChartTab === 'concurrency'
                ? 'bg-blue-600 text-white font-bold shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              Sessions & Cursors
            </button>
          </div>
        </div>

        {/* Dynamic Chart Display */}
        <div className="h-72 w-full flex items-center justify-center pt-2">
          {labels.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              {activeChartTab === 'cache' ? (
                <AreaChart data={cacheChartData}>
                  <defs>
                    <linearGradient id="cacheUsedGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="cacheDirtyGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#eab308" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#eab308" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={isDark ? 0.25 : 0.7} />
                  <XAxis dataKey="time" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} />
                  <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false}>
                    <Label value="MB" angle={-90} position="insideLeft" offset={10} fill={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{
                      backgroundColor: isDark ? '#111827' : '#ffffff',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      color: isDark ? '#f3f4f6' : '#111827',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                    formatter={(val: any, name?: any) => [`${val} MB`, name || 'Metric']}
                  />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredSeries} onHoverSeries={setHoveredSeries} />} />
                  <Area
                    type="monotone"
                    dataKey="Cache used (% of max)"
                    stroke="#f97316"
                    strokeWidth={2}
                    fill="url(#cacheUsedGrad)"
                    dot={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="Dirty (% of max)"
                    stroke="#eab308"
                    strokeWidth={2}
                    fill="url(#cacheDirtyGrad)"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="Cache Max (MB)"
                    stroke={isDark ? '#94a3b8' : '#64748b'}
                    strokeDasharray="4 4"
                    strokeWidth={1.5}
                    dot={false}
                  />
                </AreaChart>
              ) : activeChartTab === 'page_io' ? (
                <LineChart data={pageIoChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={isDark ? 0.25 : 0.7} />
                  <XAxis dataKey="time" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} />
                  <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false}>
                    <Label value="Pages / sec" angle={-90} position="insideLeft" offset={10} fill={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{
                      backgroundColor: isDark ? '#111827' : '#ffffff',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      color: isDark ? '#f3f4f6' : '#111827',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                  />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredSeries} onHoverSeries={setHoveredSeries} />} />
                  <Line type="monotone" dataKey="Pages Written/s" stroke="#a855f7" strokeWidth={2} dot={false} strokeOpacity={getOpacity(hoveredSeries, 'Pages Written/s')} />
                  <Line type="monotone" dataKey="Pages Read/s" stroke="#3b82f6" strokeWidth={2} dot={false} strokeOpacity={getOpacity(hoveredSeries, 'Pages Read/s')} />
                  <Line type="monotone" dataKey="Evicted Modified/s" stroke="#f59e0b" strokeWidth={1.5} dot={false} strokeOpacity={getOpacity(hoveredSeries, 'Evicted Modified/s')} />
                  <Line type="monotone" dataKey="Evicted Unmodified/s" stroke="#10b981" strokeWidth={1.5} dot={false} strokeOpacity={getOpacity(hoveredSeries, 'Evicted Unmodified/s')} />
                </LineChart>
              ) : activeChartTab === 'transactions' ? (
                <AreaChart data={transactionsChartData}>
                  <defs>
                    <linearGradient id="txnRollbackGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#f43f5e" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="txnCommitGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={isDark ? 0.25 : 0.7} />
                  <XAxis dataKey="time" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} />
                  <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false}>
                    <Label value="Txn / sec" angle={-90} position="insideLeft" offset={10} fill={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{
                      backgroundColor: isDark ? '#111827' : '#ffffff',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      color: isDark ? '#f3f4f6' : '#111827',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                  />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredSeries} onHoverSeries={setHoveredSeries} />} />
                  <Area type="monotone" dataKey="Txn Rolled Back/s" stroke="#f43f5e" strokeWidth={2} fill="url(#txnRollbackGrad)" dot={false} />
                  <Area type="monotone" dataKey="Txn Committed/s" stroke="#10b981" strokeWidth={2} fill="url(#txnCommitGrad)" dot={false} />
                  <Line type="monotone" dataKey="Update Conflicts/s" stroke="#eab308" strokeWidth={1.5} dot={false} />
                </AreaChart>
              ) : activeChartTab === 'journal' ? (
                <LineChart data={journalChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={isDark ? 0.25 : 0.7} />
                  <XAxis dataKey="time" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} />
                  <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{
                      backgroundColor: isDark ? '#111827' : '#ffffff',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      color: isDark ? '#f3f4f6' : '#111827',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                  />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredSeries} onHoverSeries={setHoveredSeries} />} />
                  <Line type="monotone" dataKey="Sync Avg Latency (ms)" stroke="#f59e0b" strokeWidth={2} dot={false} strokeOpacity={getOpacity(hoveredSeries, 'Sync Avg Latency (ms)')} />
                  <Line type="monotone" dataKey="Journal KB/s" stroke="#3b82f6" strokeWidth={2} dot={false} strokeOpacity={getOpacity(hoveredSeries, 'Journal KB/s')} />
                  <Line type="monotone" dataKey="Journal Syncs/s" stroke="#10b981" strokeWidth={1.5} dot={false} strokeOpacity={getOpacity(hoveredSeries, 'Journal Syncs/s')} />
                </LineChart>
              ) : (
                <LineChart data={concurrencyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={isDark ? 0.25 : 0.7} />
                  <XAxis dataKey="time" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} />
                  <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} allowDecimals={false} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{
                      backgroundColor: isDark ? '#111827' : '#ffffff',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      color: isDark ? '#f3f4f6' : '#111827',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                  />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredSeries} onHoverSeries={setHoveredSeries} />} />
                  <Line type="monotone" dataKey="Open Sessions" stroke="#3b82f6" strokeWidth={2} dot={false} strokeOpacity={getOpacity(hoveredSeries, 'Open Sessions')} />
                  <Line type="monotone" dataKey="Open Cursors" stroke="#10b981" strokeWidth={2} dot={false} strokeOpacity={getOpacity(hoveredSeries, 'Open Cursors')} />
                  <Line type="monotone" dataKey="Read Tickets" stroke="#f59e0b" strokeWidth={1.5} dot={false} strokeOpacity={getOpacity(hoveredSeries, 'Read Tickets')} />
                  <Line type="monotone" dataKey="Write Tickets" stroke="#a855f7" strokeWidth={1.5} dot={false} strokeOpacity={getOpacity(hoveredSeries, 'Write Tickets')} />
                </LineChart>
              )}
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 py-10">
              <Database className="w-8 h-8 text-gray-400 dark:text-gray-600 mb-2 opacity-50" />
              <span className="text-xs italic">No WiredTiger performance telemetry samples recorded</span>
            </div>
          )}
        </div>
      </div>

      {/* Row: Query Throughput & Average Duration */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
        {/* Query Throughput */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
            <Layers className="w-4 h-4 text-green-500 dark:text-green-400 shrink-0" />
            <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
              Operations per second (by type)
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(throughputFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={throughputFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={isDark ? 0.2 : 0.7} />
                  <XAxis dataKey="time" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} />
                  <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: isDark ? '#111827' : '#ffffff', borderColor: isDark ? '#374151' : '#e5e7eb', color: isDark ? '#f3f4f6' : '#111827', borderRadius: '8px', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredThroughput} onHoverSeries={setHoveredThroughput} />} />
                  {Object.keys(throughputFormatted[0] || {}).filter((k) => k !== 'time').map((key, i) => {
                    const color = colors[i % colors.length];
                    return (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={color}
                        strokeOpacity={getOpacity(hoveredThroughput, key)}
                        strokeWidth={hoveredThroughput === key ? 2.5 : 1.5}
                        dot={false}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">No query throughput data available</span>
            )}
          </div>
        </div>

        {/* Average Duration */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
            <HardDrive className="w-4 h-4 text-amber-500 dark:text-amber-400 shrink-0" />
            <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
              Average Duration (by Type)
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(durationFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={durationFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={isDark ? 0.2 : 0.7} />
                  <XAxis dataKey="time" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} />
                  <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: isDark ? '#111827' : '#ffffff', borderColor: isDark ? '#374151' : '#e5e7eb', color: isDark ? '#f3f4f6' : '#111827', borderRadius: '8px', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredDuration} onHoverSeries={setHoveredDuration} />} />
                  {Object.keys(durationFormatted[0] || {}).filter((k) => k !== 'time').map((key, i) => {
                    const color = colors[(i + 2) % colors.length];
                    return (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={color}
                        strokeOpacity={getOpacity(hoveredDuration, key)}
                        strokeWidth={hoveredDuration === key ? 2.5 : 1.5}
                        dot={false}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">No query duration data available</span>
            )}
          </div>
        </div>
      </div>

      {/* Engine Diagnostics & Summary Statistics Table */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
        <div className="flex items-center justify-between pb-2.5 mb-2.5 border-b border-gray-200 dark:border-gray-700/50">
          <span className="text-xs font-bold uppercase tracking-wider text-gray-800 dark:text-gray-300">
            WiredTiger Telemetry Statistics Summary
          </span>
          <span className="text-[11px] text-gray-500 dark:text-gray-400 font-mono">
            Latest vs. Peak vs. Average Sampled Metrics
          </span>
        </div>

        <div className="overflow-x-auto custom-scroll border border-gray-200 dark:border-gray-700/50 rounded-lg">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900 shadow-sm text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <th className="py-2.5 px-3 bg-gray-50 dark:bg-gray-900">Metric Description</th>
                <th className="py-2.5 px-3 text-right bg-gray-50 dark:bg-gray-900">Latest</th>
                <th className="py-2.5 px-3 text-right bg-gray-50 dark:bg-gray-900">Peak</th>
                <th className="py-2.5 px-3 text-right bg-gray-50 dark:bg-gray-900">Average</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900/40 font-mono">
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                <td className="py-2 px-3 font-semibold text-gray-800 dark:text-gray-200 font-sans">Cache Utilization (%)</td>
                <td className="py-2 px-3 text-right text-orange-600 dark:text-orange-400 font-bold">{latest?.cache_used_pct ?? 0}%</td>
                <td className="py-2 px-3 text-right text-rose-600 dark:text-rose-400">{peak?.cache_used_pct ?? 0}%</td>
                <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{avg?.cache_used_pct ?? 0}%</td>
              </tr>
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                <td className="py-2 px-3 font-semibold text-gray-800 dark:text-gray-200 font-sans">Cache Memory Used (MB)</td>
                <td className="py-2 px-3 text-right text-gray-900 dark:text-white font-bold">{latest?.cache_used_mb ?? 0} MB</td>
                <td className="py-2 px-3 text-right text-rose-600 dark:text-rose-400">{peak?.cache_used_mb ?? 0} MB</td>
                <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{avg?.cache_used_mb ?? 0} MB</td>
              </tr>
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                <td className="py-2 px-3 font-semibold text-gray-800 dark:text-gray-200 font-sans">Dirty Cache (MB)</td>
                <td className="py-2 px-3 text-right text-amber-600 dark:text-amber-400">{latest?.cache_dirty_mb ?? 0} MB</td>
                <td className="py-2 px-3 text-right text-amber-600 dark:text-amber-400">{peak?.cache_dirty_mb ?? 0} MB</td>
                <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{avg?.cache_dirty_mb ?? 0} MB</td>
              </tr>
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                <td className="py-2 px-3 font-semibold text-gray-800 dark:text-gray-200 font-sans">Pages Written / sec</td>
                <td className="py-2 px-3 text-right text-purple-600 dark:text-purple-400 font-bold">{latest?.pages_written_per_sec ?? 0}/s</td>
                <td className="py-2 px-3 text-right text-purple-600 dark:text-purple-300">{peak?.pages_written_per_sec ?? 0}/s</td>
                <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{avg?.pages_written_per_sec ?? 0}/s</td>
              </tr>
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                <td className="py-2 px-3 font-semibold text-gray-800 dark:text-gray-200 font-sans">Aborted transactions / sec</td>
                <td className="py-2 px-3 text-right text-rose-600 dark:text-rose-400 font-bold">{latest?.txn_rolled_back_per_sec ?? 0}/s</td>
                <td className="py-2 px-3 text-right text-rose-600 dark:text-rose-300">{peak?.txn_rolled_back_per_sec ?? 0}/s</td>
                <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{avg?.txn_rolled_back_per_sec ?? 0}/s</td>
              </tr>
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                <td className="py-2 px-3 font-semibold text-gray-800 dark:text-gray-200 font-sans">Journal Sync Latency (ms)</td>
                <td className="py-2 px-3 text-right text-amber-600 dark:text-amber-400 font-bold">{latest?.journal_sync_avg_ms ?? 0} ms</td>
                <td className="py-2 px-3 text-right text-rose-600 dark:text-rose-400">{peak?.journal_sync_avg_ms ?? 0} ms</td>
                <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{avg?.journal_sync_avg_ms ?? 0} ms</td>
              </tr>
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                <td className="py-2 px-3 font-semibold text-gray-800 dark:text-gray-200 font-sans">Checkpoints Succeeded</td>
                <td className="py-2 px-3 text-right text-emerald-600 dark:text-emerald-400 font-bold">{latest?.checkpoints_succeeded ?? 0}</td>
                <td className="py-2 px-3 text-right text-emerald-600 dark:text-emerald-400">{peak?.checkpoints_succeeded ?? 0}</td>
                <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{avg?.checkpoints_succeeded ?? 0}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MongoWiredTigerView;

